import { useCallback } from 'react';
import { Row } from '@tanstack/react-table';
import { Glyphicon } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import ArrowClockwiseIcon from 'assets/images/icons/arrow-clockwise.svg?react';
import ClockHistoryIcon from 'assets/images/icons/clock-history.svg?react';
import PencilSquareIcon from 'assets/images/icons/pencil-square.svg?react';
import SendIcon from 'assets/images/icons/send.svg?react';
import Button from 'components/button/Button';
import Tooltip from 'components/tooltip/Tooltip';
import { ScheduledMessage } from 'models/automation/ScheduledMessages';
import { TriggerState } from 'models/automation/Triggers';
import { useNavigate } from 'react-router-dom';
import useScheduledMessagesCancelModal from '../../modal/cancel/useScheduledMessagesCancelModal';
import useScheduledMessagesReactivateModal from '../../modal/reactivate/useScheduledMessagesReactivateModal';
import useScheduledMessagesSendNowModal from '../../modal/sendNow/useScheduledMessagesSendNowModal';
import { ActionsCellStyled } from '../ScheduledMessagesTable.styles';

const ActionsCell = ({ row }: { row: Row<ScheduledMessage> }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openCancelScheduledMessagesModal } =
    useScheduledMessagesCancelModal();
  const { openReactivateScheduledMessagesModal } =
    useScheduledMessagesReactivateModal();
  const { openSendNowScheduledMessagesModal } =
    useScheduledMessagesSendNowModal();
  const scheduledMessage = row.original;
  const { state, uid: scheduledMessageUid } = scheduledMessage;
  const isCancelled = state === TriggerState.CANCELLED;

  const navigateToEditPage = useCallback(() => {
    navigate(`./${scheduledMessageUid}`);
  }, [navigate, scheduledMessageUid]);

  const navigateToReschedulePage = useCallback(() => {
    navigate(`./${scheduledMessageUid}/reschedule`);
  }, [navigate, scheduledMessageUid]);

  if (isCancelled) {
    return (
      <ActionsCellStyled>
        <Tooltip id="row-edit-message-button" text={t('common.edit')}>
          <Button
            bsStyle="link"
            data-testid="row-edit-message-button"
            onClick={navigateToEditPage}
          >
            <PencilSquareIcon aria-label={t('common.edit')} />
          </Button>
        </Tooltip>

        <Tooltip
          id="row-reactivate-message-button"
          text={t('common.reactivate')}
        >
          <Button
            bsStyle="link"
            data-testid="row-reactivate-message-button"
            onClick={() =>
              openReactivateScheduledMessagesModal({ scheduledMessage })
            }
          >
            <ArrowClockwiseIcon aria-label={t('common.reactivate')} />
          </Button>
        </Tooltip>
      </ActionsCellStyled>
    );
  }

  return (
    <ActionsCellStyled>
      <Tooltip id="row-edit-message-button" text={t('common.edit')}>
        <Button
          bsStyle="link"
          data-testid="row-edit-message-button"
          onClick={navigateToEditPage}
        >
          <PencilSquareIcon aria-label={t('common.edit')} />
        </Button>
      </Tooltip>

      <Tooltip
        id="row-send-message-button"
        text={t('pageAutomations.scheduledMessages.table.sendNow')}
      >
        <Button
          bsStyle="link"
          data-testid="row-send-message-button"
          onClick={() =>
            openSendNowScheduledMessagesModal({ scheduledMessage })
          }
        >
          <SendIcon
            aria-label={t('pageAutomations.scheduledMessages.table.sendNow')}
          />
        </Button>
      </Tooltip>

      <Tooltip id="row-reschedule-message-button" text={t('common.reschedule')}>
        <Button
          bsStyle="link"
          data-testid="row-reschedule-message-button"
          onClick={navigateToReschedulePage}
        >
          <ClockHistoryIcon aria-label={t('common.reschedule')} />
        </Button>
      </Tooltip>

      <Tooltip id="row-cancel-message-button" text={t('common.cancel')}>
        <Button
          bsStyle="link"
          data-testid="row-cancel-message-button"
          onClick={() => openCancelScheduledMessagesModal({ scheduledMessage })}
        >
          <Glyphicon glyph="remove-sign" aria-label={t('common.cancel')} />
        </Button>
      </Tooltip>
    </ActionsCellStyled>
  );
};

export default ActionsCell;
